import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  Grid,
  TextField,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Chip,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalCarWashIcon from "@mui/icons-material/LocalCarWash";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getBookings, updateBookingStatus, deleteBooking, getCachedBookings } from "../../services/api/getAll";
import logoPrint from "../../assets/logo-print.png";

const statusOptions = [
  { label: "Pending", value: "Pending" },
  { label: "Confirmed", value: "Confirmed" },
  { label: "Completed", value: "Completed" },
  { label: "Cancelled", value: "Cancelled" },
];

const BookingsAdminScreen = () => {
  const [bookings, setBookings] = useState<any[]>(() => {
    try {
      const cached = getCachedBookings();
      return cached || [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(() => {
    try {
      const cached = getCachedBookings();
      return cached.length === 0;
    } catch (e) {
      return true;
    }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [updatingId, setUpdatingId] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<any>(null);

  // Row Selection & Print States (at top level)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [printOrders, setPrintOrders] = useState<any[] | null>(null);
  


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetchError, setFetchError] = useState(false);

  // Custom Alert and Confirm States
  const [customAlert, setCustomAlert] = useState<{ open: boolean; title: string; message: string } | null>(null);
  const [customConfirm, setCustomConfirm] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void } | null>(null);

  const showCustomAlert = (message: string, title = "Alert") => {
    setCustomAlert({ open: true, title, message });
  };

  const showCustomConfirm = (message: string, onConfirm: () => void, title = "Confirm") => {
    setCustomConfirm({ open: true, title, message, onConfirm });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [tableWidth, setTableWidth] = useState(0);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const isScrollingRef = useRef(false);

  const handleTopScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      const targetScroll = topScrollRef.current.scrollLeft;
      if (tableContainerRef.current.scrollLeft !== targetScroll) {
        tableContainerRef.current.scrollLeft = targetScroll;
      }
    }
  };

  const handleTableScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      const targetScroll = tableContainerRef.current.scrollLeft;
      if (topScrollRef.current.scrollLeft !== targetScroll) {
        topScrollRef.current.scrollLeft = targetScroll;
      }
    }
  };


  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);


  
  // Track previous bookings count to detect new ones
  const prevBookingsCountRef = useRef<number | null>(null);

  const fetchBookingsList = async (showProgress = false) => {
    if (showProgress) setRefreshing(true);
    try {
      const res: any = await getBookings();
      if (res && res.success === false) {
        setFetchError(true);
        if (bookings.length > 0) {
          return; // Keep existing bookings
        }
      } else {
        setFetchError(false);
      }
      const items = res?.items || [];
      
      // Sort bookings by ID (descending) or createdAt (descending)
      const sorted = [...items].sort((a: any, b: any) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : Number(a.id) || 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : Number(b.id) || 0;
        return dateB - dateA;
      });

      // Avoid unnecessary state re-renders if list hasn't changed
      setBookings((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(sorted)) {
          return prev;
        }
        return sorted;
      });

      // Play audio chime if a new booking is added (count increases)
      if (prevBookingsCountRef.current !== null && sorted.length > prevBookingsCountRef.current) {
        triggerSoundAlert();
        triggerTabFlash();
      }
      prevBookingsCountRef.current = sorted.length;
    } catch (e) {
      console.error("Failed to load bookings in admin panel:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Sound generator using native Web Audio API (cross-browser robust, no files needed)
  const triggerSoundAlert = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      
      // Play a premium double chime
      const playTone = (freq: number, start: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.3, start);
        gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
        osc.start(start);
        osc.stop(start + duration);
      };
      
      playTone(523.25, ctx.currentTime, 0.2); // C5
      playTone(659.25, ctx.currentTime + 0.15, 0.4); // E5
    } catch (err) {
      console.error("Audio Context playback blocked/failed:", err);
    }
  };

  const triggerTabFlash = () => {
    let isFlash = false;
    const originalTitle = document.title;
    const interval = setInterval(() => {
      document.title = isFlash ? "🔴 NEW BOOKING!" : originalTitle;
      isFlash = !isFlash;
    }, 1000);

    // Stop flashing when user clicks/moves in window
    const stopFlash = () => {
      clearInterval(interval);
      document.title = originalTitle;
      window.removeEventListener("mousemove", stopFlash);
      window.removeEventListener("click", stopFlash);
    };
    window.addEventListener("mousemove", stopFlash);
    window.addEventListener("click", stopFlash);
  };

  useEffect(() => {
    fetchBookingsList();
    
    // Live Polling every 25 seconds
    const timer = setInterval(() => {
      fetchBookingsList(false);
    }, 25000);

    return () => clearInterval(timer);
  }, []);

  const handleStatusChange = async (id: any, status: string) => {
    setUpdatingId(id);
    try {
      await updateBookingStatus(id, status);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch (e) {
      console.error("Failed to update booking status:", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteBooking = (id: any) => {
    showCustomConfirm("Are you sure you want to delete/archive this booking?", async () => {
      // Optimistic deletion: remove row instantly from UI (< 10ms)
      setBookings((prev) => prev.filter((b) => b.id !== id));
      if (prevBookingsCountRef.current !== null) {
        prevBookingsCountRef.current = Math.max(0, prevBookingsCountRef.current - 1);
      }
      try {
        await deleteBooking(id);
      } catch (e) {
        console.error("Failed to delete booking:", e);
      }
    }, "Delete Booking");
  };

  // Dynamically unpack any merged/multi-car booking (old or new) into separate rows for the table
  const expandedBookings = React.useMemo(() => {
    const result: any[] = [];
    bookings.forEach((b: any) => {
      // Try to parse carsDetails if present
      let carsDetailsArr: any[] = [];
      try {
        if (b.carsDetails) {
          carsDetailsArr = typeof b.carsDetails === "string" ? JSON.parse(b.carsDetails) : b.carsDetails;
        }
      } catch (e) { /* ignore */ }

      if (carsDetailsArr.length > 1) {
        carsDetailsArr.forEach((cd: any) => {
          result.push({
            ...b,
            id: `${b.id}_car_${cd.carIndex}`,
            clientName: (b.clientName || "").includes("سيارة") || (b.clientName || "").includes("Car")
              ? b.clientName
              : `${b.clientName} (سيارة ${cd.carIndex}/${carsDetailsArr.length})`,
            serviceName: cd.serviceName || b.serviceName,
            packageName: cd.packageName || b.packageName,
            carBrand: cd.brand || b.carBrand,
            carModel: cd.model || b.carModel,
            carColor: cd.color || b.carColor,
            carPlateLetters: cd.plateLetters || b.carPlateLetters,
            carPlateNumbers: cd.plateNumbers || b.carPlateNumbers,
            carFullDetails: `${cd.brand || ""} ${cd.model || ""} ${cd.color ? `(${cd.color})` : ""}`.trim(),
            packagePrice: cd.packagePrice || b.packagePrice,
            price: cd.packagePrice || b.price,
            totalPrice: cd.packagePrice || b.totalPrice,
            carsCount: 1,
            carsDetails: null,
          });
        });
        return;
      }

      // Check if old booking has ' | ' in packageName or carBrand
      const pkgs = b.packageName ? String(b.packageName).split(" | ").filter(Boolean) : [];
      const brands = b.carBrand ? String(b.carBrand).split(" | ").filter(Boolean) : [];
      const models = b.carModel ? String(b.carModel).split(" | ").filter(Boolean) : [];
      const colors = b.carColor ? String(b.carColor).split(" | ").filter(Boolean) : [];

      const maxCount = Math.max(pkgs.length, brands.length, models.length);

      if (maxCount > 1) {
        for (let i = 0; i < maxCount; i++) {
          result.push({
            ...b,
            id: `${b.id}_car_${i + 1}`,
            clientName: (b.clientName || "").includes("سيارة") || (b.clientName || "").includes("Car")
              ? b.clientName
              : `${b.clientName} (سيارة ${i + 1}/${maxCount})`,
            serviceName: b.serviceName,
            packageName: pkgs[i] || pkgs[0] || b.packageName,
            carBrand: brands[i] || brands[0] || b.carBrand,
            carModel: models[i] || models[0] || b.carModel,
            carColor: colors[i] || colors[0] || b.carColor,
            packagePrice: b.totalPrice ? Math.round(Number(b.totalPrice) / maxCount) : b.packagePrice,
            price: b.totalPrice ? Math.round(Number(b.totalPrice) / maxCount) : b.price,
            totalPrice: b.totalPrice ? Math.round(Number(b.totalPrice) / maxCount) : b.totalPrice,
            carsCount: 1,
            carsDetails: null,
          });
        }
        return;
      }

      result.push(b);
    });
    return result;
  }, [bookings]);

  const filteredBookings = expandedBookings.filter((booking) => {
    const matchesSearch =
      String(booking.clientName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(booking.clientPhone || "").includes(searchQuery) ||
      String(booking.serviceName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(booking.packageName || "").toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "All" || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.offsetWidth);
      }
    };
    
    const timer = setTimeout(updateWidth, 100);
    window.addEventListener("resize", updateWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateWidth);
    };
  }, [filteredBookings, page, rowsPerPage]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress size={50} sx={{ color: "#FFAE00" }} />
      </Box>
    );
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>, currentDisplayedItems: any[]) => {
    if (event.target.checked) {
      const pageItemIds = currentDisplayedItems.map((b) => String(b.id));
      setSelectedIds(Array.from(new Set([...selectedIds, ...pageItemIds])));
    } else {
      const pageItemIds = new Set(currentDisplayedItems.map((b) => String(b.id)));
      setSelectedIds(selectedIds.filter((id) => !pageItemIds.has(id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const strId = String(id);
    if (selectedIds.includes(strId)) {
      setSelectedIds(selectedIds.filter((item) => item !== strId));
    } else {
      setSelectedIds([...selectedIds, strId]);
    }
  };

  // Export to Excel / CSV with UTF-8 Arabic support
  const handleExportToExcel = (targetItems?: any[]) => {
    const items = targetItems && targetItems.length > 0 ? targetItems : (
      selectedIds.length > 0
        ? expandedBookings.filter((b) => selectedIds.includes(String(b.id)))
        : filteredBookings
    );

    if (items.length === 0) {
      showCustomAlert("No orders available to export!", "Export Error");
      return;
    }

    let csv = "\uFEFF"; // UTF-8 BOM for Excel
    const headers = [
      "Order ID",
      "Client Name",
      "Phone Number",
      "Service Name",
      "Package Name",
      "Car Brand",
      "Car Model",
      "Car Color",
      "Plate Letters",
      "Plate Numbers",
      "Price (EGP)",
      "Order Date & Time",
      "Requested Service Time",
      "Location / Address",
      "Notes",
      "Status"
    ];
    csv += headers.map((h) => `"${h}"`).join(",") + "\n";

    items.forEach((b: any) => {
      const row = [
        b.id || "",
        b.clientName || "",
        b.clientPhone || "",
        b.serviceName || "",
        b.packageName || "",
        b.carBrand || "",
        b.carModel || "",
        b.carColor || "",
        b.carPlateLetters || "",
        b.carPlateNumbers || "",
        b.totalPrice || b.packagePrice || b.price || "",
        (() => {
          const dateVal = b.createdAt || (!isNaN(Number(b.id)) ? Number(b.id) : null);
          if (!dateVal) return "";
          const d = new Date(dateVal);
          return isNaN(d.getTime()) ? "" : d.toLocaleString();
        })(),
        (() => {
          const dateVal = b.dateTime;
          if (!dateVal) return "";
          const d = new Date(dateVal);
          return isNaN(d.getTime()) ? dateVal : d.toLocaleString();
        })(),
        b.location || "",
        (b.notes || "").replace(/"/g, '""'),
        b.status || "Pending"
      ];
      csv += row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",") + "\n";
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TowHelp_Orders_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrintSelected = () => {
    const itemsToPrint = expandedBookings.filter((b) => selectedIds.includes(String(b.id)));
    if (itemsToPrint.length === 0) {
      showCustomAlert("Please select at least one order to print!", "Print Selection");
      return;
    }
    setPrintOrders(itemsToPrint);
  };

  const handlePrintSingle = (order: any) => {
    setPrintOrders([order]);
  };

  return (
    <Box sx={{ p: { xs: 0, md: 1 }, minHeight: "100vh", color: "#fff" }}>
      {/* Title section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" fontWeight="800" sx={{ color: "#FFAE00" }}>
            Service Bookings
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExportToExcel()}
            sx={{
              background: "linear-gradient(135deg, #107c41, #1f9a55)",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "10px",
              py: 0.6,
              "&:hover": {
                background: "#107c41",
              },
            }}
          >
            Export Excel (تصدير إكسيل)
          </Button>
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrintSelected}
              sx={{
                background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
                color: "#000",
                fontWeight: "bold",
                borderRadius: "10px",
                py: 0.6,
              }}
            >
              Print Selected ({selectedIds.length}) (طباعة)
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={() => fetchBookingsList(true)}
            disabled={refreshing}
            sx={{
              borderColor: "#FFAE00",
              color: "#FFAE00",
              fontWeight: "bold",
              borderRadius: "10px",
              py: 0.6,
              "&:hover": {
                borderColor: "#FF8F00",
                background: "rgba(255, 174, 0, 0.08)",
              },
            }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </Box>

      {/* Filter and search controls */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by client name, phone, service, package..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              input: { color: "#fff", py: 0.8 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                "& fieldset": { borderColor: "rgba(255,174,0,0.15)" },
                "&:hover fieldset": { borderColor: "#FFAE00" },
                "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Tabs
            value={statusFilter}
            onChange={(_, val) => setStatusFilter(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              "& .MuiTabs-indicator": { backgroundColor: "#FFAE00" },
              "& .MuiTab-root": {
                color: "#888",
                fontWeight: "bold",
                minHeight: 36,
                py: 0.5,
                fontSize: "0.8rem",
                "&.Mui-selected": { color: "#FFAE00" },
              },
            }}
          >
            <Tab label="All Bookings" value="All" />
            <Tab label="Pending" value="Pending" />
            <Tab label="Confirmed" value="Confirmed" />
            <Tab label="Completed" value="Completed" />
            <Tab label="Cancelled" value="Cancelled" />
          </Tabs>
        </Grid>
      </Grid>

      {/* Bulk Actions Banner */}
      {selectedIds.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            background: "linear-gradient(90deg, rgba(255,174,0,0.15), rgba(255,145,0,0.05))",
            border: "1px solid rgba(255,174,0,0.4)",
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body1" fontWeight="bold" sx={{ color: "#FFAE00" }}>
            ✅ Selected Orders: {selectedIds.length}
          </Typography>
          <Box display="flex" gap={1.5} alignItems="center">
            <Button
              size="small"
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={() => handleExportToExcel()}
              sx={{ background: "#107c41", color: "#fff", fontWeight: "bold" }}
            >
              Export Selected to Excel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintSelected}
              sx={{ background: "#FFAE00", color: "#000", fontWeight: "bold" }}
            >
              Print Selected Job Orders
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setSelectedIds([])}
              sx={{ borderColor: "#666", color: "#ccc" }}
            >
              Deselect All
            </Button>
          </Box>
        </Paper>
      )}

      {/* Bookings List Table */}
      {filteredBookings.length === 0 ? (
        <Paper
          sx={{
            p: 8,
            textAlign: "center",
            background: "rgba(18, 18, 18, 0.4)",
            border: "1px solid rgba(255,174,0,0.08)",
            borderRadius: "24px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#888" }}>
            No bookings found.
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            background: "#18181B",
            borderRadius: "16px",
            border: "1px solid #27272A",
            overflow: "hidden",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.4)",
          }}
        >
          {/* Top Dummy Scrollbar */}
          <Box 
            ref={topScrollRef} 
            onScroll={handleTopScroll} 
            sx={{ 
              overflowX: "auto", 
              overflowY: "hidden", 
              width: "100%", 
              backgroundColor: "#09090B",
              borderBottom: "1px solid #27272A",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#FFAE00",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#18181B",
              }
            }}
          >
            <Box sx={{ width: tableWidth, height: "1px" }} />
          </Box>

          <TableContainer ref={tableContainerRef} onScroll={handleTableScroll} sx={{ overflowX: "auto", width: "100%", display: "block" }}>
            <Table ref={tableRef} sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#09090B", borderBottom: "2px solid #27272A", width: "40px", p: 1 }}>
                    <Checkbox
                      checked={
                        filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 &&
                        filteredBookings
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .every((b) => selectedIds.includes(String(b.id)))
                      }
                      indeterminate={
                        filteredBookings
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .some((b) => selectedIds.includes(String(b.id))) &&
                        !filteredBookings
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .every((b) => selectedIds.includes(String(b.id)))
                      }
                      onChange={(e) =>
                        handleSelectAll(
                          e,
                          filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        )
                      }
                      sx={{ color: "#FFAE00", "&.Mui-checked": { color: "#FFAE00" } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Service & Package
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Price
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Date & Time
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Car Details
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Notes
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Status
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: "#09090B", color: "#FFAE00", fontWeight: "bold", borderBottom: "2px solid #27272A", whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBookings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((booking) => {
                    const statusColor =
                      booking.status === "Confirmed"
                        ? "#4caf50"
                        : booking.status === "Completed"
                        ? "#2196f3"
                        : booking.status === "Cancelled"
                        ? "#f44336"
                        : "#ff9800"; // Pending

                    const isWash = booking.serviceId === 7;
                    const isSelected = selectedIds.includes(String(booking.id));

                    return (
                      <TableRow
                        key={booking.id}
                        sx={{
                          backgroundColor: isSelected ? "rgba(255, 174, 0, 0.08)" : "transparent",
                          "&:hover": { backgroundColor: isSelected ? "rgba(255, 174, 0, 0.12)" : "rgba(255, 174, 0, 0.02)" },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        {/* Checkbox */}
                        <TableCell sx={{ borderBottom: "1px solid #27272A", width: "40px", p: 1 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectOne(String(booking.id))}
                            sx={{ color: "#FFAE00", "&.Mui-checked": { color: "#FFAE00" } }}
                          />
                        </TableCell>

                        {/* Client Name */}
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #27272A", fontWeight: "bold", whiteSpace: "nowrap" }}>
                          {booking.clientName}
                        </TableCell>

                        {/* Contact */}
                        <TableCell sx={{ borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PhoneIcon sx={{ color: "#FFAE00", fontSize: 14 }} />
                            <Typography
                              component="a"
                              href={`tel:${booking.clientPhone}`}
                              sx={{ color: "#FFAE00", textDecoration: "none", fontSize: "0.875rem", fontWeight: 500 }}
                            >
                              {booking.clientPhone}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Service & Package */}
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          {(() => {
                            let carsDetailsArr: any[] = [];
                            try {
                              if (booking.carsDetails) {
                                carsDetailsArr = typeof booking.carsDetails === "string" ? JSON.parse(booking.carsDetails) : booking.carsDetails;
                              }
                            } catch (e) { /* ignore */ }

                            if (carsDetailsArr.length > 0) {
                              return (
                                <Box>
                                  {carsDetailsArr.map((cd: any, i: number) => (
                                    <Box key={i} display="flex" alignItems="center" gap={0.8} sx={{ mb: i < carsDetailsArr.length - 1 ? 0.75 : 0 }}>
                                      {cd.serviceId === 7 ? (
                                        <LocalCarWashIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                      ) : (
                                        <DirectionsCarIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                      )}
                                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                                        {carsDetailsArr.length > 1 ? `Car #${cd.carIndex}: ` : ""}
                                        {cd.serviceName}{cd.packageName ? ` - ${cd.packageName}` : ""}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            }

                            // Fallback if packageName contains multiple packages separated by |
                            const pkgs = (booking.packageName || "").split(" | ").filter(Boolean);
                            if (pkgs.length > 1) {
                              return (
                                <Box>
                                  {pkgs.map((pkg: string, i: number) => (
                                    <Box key={i} display="flex" alignItems="center" gap={0.8} sx={{ mb: i < pkgs.length - 1 ? 0.75 : 0 }}>
                                      <LocalCarWashIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                                        Car #{i + 1}: {booking.serviceName} - {pkg}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            }

                            // Single service/package fallback
                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                {isWash ? (
                                  <LocalCarWashIcon sx={{ color: "#FFAE00", fontSize: 16 }} />
                                ) : (
                                  <DirectionsCarIcon sx={{ color: "#FFAE00", fontSize: 16 }} />
                                )}
                                <Typography variant="body2" fontWeight="bold">
                                  {booking.serviceName}{booking.packageName ? ` - ${booking.packageName}` : ""}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </TableCell>

                        {/* Price */}
                        <TableCell sx={{ color: "#fff", borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          {(() => {
                            let carsDetailsArr: any[] = [];
                            try {
                              if (booking.carsDetails) {
                                carsDetailsArr = typeof booking.carsDetails === "string" ? JSON.parse(booking.carsDetails) : booking.carsDetails;
                              }
                            } catch (e) { /* ignore */ }

                            if (carsDetailsArr.length > 1 && carsDetailsArr.some((cd: any) => cd.packagePrice)) {
                              return (
                                <Box>
                                  {carsDetailsArr.map((cd: any, i: number) => (
                                    <Typography key={i} variant="caption" sx={{ color: "#aaa", display: "block", whiteSpace: "nowrap" }}>
                                      Car #{cd.carIndex}: {cd.packagePrice ? `${cd.packagePrice} EGP` : "—"}
                                    </Typography>
                                  ))}
                                  <Typography variant="body2" fontWeight="bold" sx={{ color: "#FFAE00", display: "block", mt: 0.3, borderTop: "1px dashed rgba(255,174,0,0.3)", pt: 0.3 }}>
                                    Total: {booking.totalPrice || booking.packagePrice || booking.price} EGP
                                  </Typography>
                                </Box>
                              );
                            }

                            return (booking.totalPrice || booking.packagePrice || booking.price) ? (
                              <Typography variant="body2" fontWeight="bold" sx={{ color: "#FFAE00" }}>
                                {booking.totalPrice || booking.packagePrice || booking.price} EGP
                              </Typography>
                            ) : (
                              <span style={{ color: "#555" }}>—</span>
                            );
                          })()}
                        </TableCell>

                        {/* Date & Time */}
                        <TableCell sx={{ color: "#ddd", borderBottom: "1px solid #27272A", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {/* Request / Order creation time */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CalendarMonthIcon sx={{ color: "#aaa", fontSize: 13 }} />
                              <Typography variant="caption" sx={{ color: "#aaa", display: "inline-flex", gap: 0.5, fontSize: "0.75rem" }}>
                                <strong style={{ color: "#ccc" }}>Order:</strong>
                                {(() => {
                                  const dateVal = booking.createdAt || (!isNaN(Number(booking.id)) ? Number(booking.id) : null);
                                  if (!dateVal) return "—";
                                  const d = new Date(dateVal);
                                  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
                                })()}
                              </Typography>
                            </Box>
                            {/* Requested Service execution time */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTimeIcon sx={{ color: "#FFAE00", fontSize: 13 }} />
                              <Typography variant="body2" sx={{ color: "#FFAE00", fontWeight: "bold", display: "inline-flex", gap: 0.5, fontSize: "0.8rem" }}>
                                <strong>Service:</strong>
                                {(() => {
                                  const dateVal = booking.dateTime;
                                  if (!dateVal) return "—";
                                  const d = new Date(dateVal);
                                  return isNaN(d.getTime()) ? dateVal : d.toLocaleString();
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Car Details */}
                        <TableCell sx={{ color: "#ddd", borderBottom: "1px solid #27272A", fontSize: "0.85rem", whiteSpace: "nowrap", direction: "rtl", textAlign: "right" }}>
                          {(() => {
                            let carsDetailsArr: any[] = [];
                            try {
                              if (booking.carsDetails) {
                                carsDetailsArr = typeof booking.carsDetails === "string" ? JSON.parse(booking.carsDetails) : booking.carsDetails;
                              }
                            } catch (e) { /* ignore */ }

                            if (carsDetailsArr.length > 0) {
                              return (
                                <Box>
                                  {carsDetailsArr.map((cd: any, i: number) => (
                                    <Box key={i} display="flex" alignItems="center" gap={0.8} sx={{ mb: i < carsDetailsArr.length - 1 ? 0.75 : 0 }}>
                                      <DirectionsCarIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                      <Typography variant="body2" sx={{ color: "#ddd", whiteSpace: "nowrap", fontWeight: 500 }}>
                                        {cd.brand || ""} {cd.model || ""} {cd.color ? `(${cd.color})` : ""}
                                        {(cd.plateLetters || cd.plateNumbers) ? ` • لوحة: ${cd.plateLetters || ""} ${cd.plateNumbers || ""}`.trim() : ""}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            }

                            // Fallback for multi-car without JSON (split by |)
                            const brands = (booking.carBrand || "").split(" | ").filter(Boolean);
                            const models = (booking.carModel || "").split(" | ").filter(Boolean);
                            const colors = (booking.carColor || "").split(" | ").filter(Boolean);
                            const maxCars = Math.max(brands.length, models.length);

                            if (maxCars > 1) {
                              return (
                                <Box>
                                  {Array.from({ length: maxCars }).map((_, i) => (
                                    <Box key={i} display="flex" alignItems="center" gap={0.8} sx={{ mb: i < maxCars - 1 ? 0.75 : 0 }}>
                                      <DirectionsCarIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                      <Typography variant="body2" sx={{ color: "#ddd", whiteSpace: "nowrap", fontWeight: 500 }}>
                                        {brands[i] || ""} {models[i] || ""} {colors[i] ? `(${colors[i]})` : ""}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            }

                            // Single car fallback
                            if (booking.carFullDetails || booking.carModel || booking.carBrand) {
                              return (
                                <Box display="flex" alignItems="center" gap={0.8} sx={{ whiteSpace: "nowrap" }}>
                                  <DirectionsCarIcon sx={{ color: "#FFAE00", fontSize: 15, flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ color: "#ddd", whiteSpace: "nowrap", fontWeight: 500 }}>
                                    {booking.carFullDetails || [
                                      booking.carBrand,
                                      booking.carModel,
                                      booking.carColor ? `(${booking.carColor})` : "",
                                      (booking.carPlateLetters || booking.carPlateNumbers) ? `• لوحة: ${booking.carPlateLetters} ${booking.carPlateNumbers}` : ""
                                    ].filter(Boolean).join(" ")}
                                  </Typography>
                                </Box>
                              );
                            }

                            return <span style={{ color: "#555" }}>—</span>;
                          })()}
                        </TableCell>

                        {/* Location */}
                        <TableCell sx={{ color: "#ddd", borderBottom: "1px solid #27272A", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                          {booking.location ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LocationOnIcon sx={{ color: "#aaa", fontSize: 14 }} />
                              <span>{booking.location}</span>
                            </Box>
                          ) : (
                            <span style={{ color: "#555" }}>—</span>
                          )}
                        </TableCell>

                        {/* Notes */}
                        <TableCell sx={{ borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          {booking.notes ? (
                            <Tooltip title={booking.notes} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "help",
                                  color: "#bbb",
                                  fontStyle: "italic",
                                }}
                              >
                                {booking.notes}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <span style={{ color: "#555" }}>—</span>
                          )}
                        </TableCell>

                        {/* Status Select Controller */}
                        <TableCell sx={{ borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Select
                              size="small"
                              value={booking.status || "Pending"}
                              onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                              disabled={updatingId === booking.id}
                              sx={{
                                height: 32,
                                minWidth: 110,
                                fontSize: "0.8rem",
                                fontWeight: "bold",
                                color: statusColor,
                                backgroundColor: "rgba(0,0,0,0.25)",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.08)" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: statusColor },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: statusColor },
                                "& .MuiSvgIcon-root": { color: statusColor }
                              }}
                            >
                              {statusOptions.map((opt) => (
                                <MenuItem
                                  key={opt.value}
                                  value={opt.value}
                                  sx={{
                                    fontWeight: "bold",
                                    color:
                                      opt.value === "Confirmed"
                                        ? "#4caf50"
                                        : opt.value === "Completed"
                                        ? "#2196f3"
                                        : opt.value === "Cancelled"
                                        ? "#f44336"
                                        : "#ff9800",
                                  }}
                                >
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {updatingId === booking.id && (
                              <CircularProgress size={16} sx={{ color: "#FFAE00" }} />
                            )}
                          </Box>
                        </TableCell>

                        {/* Action Buttons */}
                        <TableCell align="center" sx={{ borderBottom: "1px solid #27272A", whiteSpace: "nowrap" }}>
                          <Tooltip title="Print Job Order (أمر عمل للمناولة والعمال)" arrow>
                            <IconButton
                              onClick={() => handlePrintSingle(booking)}
                              size="small"
                              sx={{
                                color: "#FFAE00",
                                mr: 1,
                                "&:hover": {
                                  backgroundColor: "rgba(255, 174, 0, 0.15)",
                                },
                              }}
                            >
                              <PrintIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteBooking(booking.id)}
                            disabled={deletingId === booking.id}
                            size="small"
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(244, 67, 54, 0.08)",
                              },
                            }}
                          >
                            {deletingId === booking.id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredBookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: "#aaa",
              borderTop: "1px solid #27272A",
              "& .MuiTablePagination-selectIcon": { color: "#FFAE00" },
              "& .MuiIconButton-root": {
                color: "#FFAE00",
                "&.Mui-disabled": { color: "#444" },
              },
            }}
          />
        </Paper>
      )}

      {/* Printable Job Order Sheet Dialog */}
      <Dialog
        open={Boolean(printOrders)}
        onClose={() => setPrintOrders(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: "#fff",
            color: "#000",
            p: 1,
          },
        }}
      >
        <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography component="span" variant="h6" fontWeight="bold" sx={{ color: "#111" }}>
            🖨️ أمر عمل وتجهيز (Job Order Receipt)
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ background: "linear-gradient(135deg, #FFAE00, #FF8F00)", color: "#000", fontWeight: "bold" }}
            >
              طباعة الآن (Print)
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => setPrintOrders(null)}>
              إغلاق
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f5f5f5" }}>
          <Box id="printable-job-order">
            <style>{`
              @media print {
                body * {
                  visibility: hidden !important;
                }
                #printable-job-order, #printable-job-order * {
                  visibility: visible !important;
                }
                #printable-job-order {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 100% !important;
                  padding: 20px !important;
                  background: #fff !important;
                  color: #000 !important;
                }
                .no-print {
                  display: none !important;
                }
              }
            `}</style>

            {printOrders?.map((order: any, idx: number) => (
              <Paper
                key={idx}
                elevation={0}
                dir="rtl"
                sx={{
                  p: 3,
                  mb: idx < printOrders.length - 1 ? 4 : 0,
                  border: "2px solid #222",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  color: "#000",
                  textAlign: "right",
                  pageBreakAfter: idx < printOrders.length - 1 ? "always" : "auto",
                }}
              >
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2} mb={2} sx={{ borderBottom: "3px solid #FFAE00" }}>
                  {/* Title (Renders on the right in RTL) */}
                  <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#111", fontFamily: "Poppins, Ozen" }}>
                      أمر عمل وتسليم ورشة
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: "#444", display: "block", mt: 0.5, fontFamily: "Poppins, Ozen" }}>
                      رقم الطلب: #{order.id}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: "block", fontFamily: "Poppins, Ozen" }}>
                      تاريخ الطلب: {(() => {
                        const dateVal = order.createdAt || (!isNaN(Number(order.id)) ? Number(order.id) : null);
                        if (!dateVal) return "—";
                        const d = new Date(dateVal);
                        return isNaN(d.getTime()) ? "—" : d.toLocaleString("ar-EG");
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#e65100", fontWeight: "bold", display: "block", mt: 0.5, fontFamily: "Poppins, Ozen" }}>
                      موعد الخدمة المطلوب: {(() => {
                        const dateVal = order.dateTime;
                        if (!dateVal) return "غير محدد";
                        const d = new Date(dateVal);
                        return isNaN(d.getTime()) ? dateVal : d.toLocaleString("ar-EG");
                      })()}
                    </Typography>
                  </Box>

                  {/* Logo (Renders on the left in RTL) */}
                  <Box display="flex" alignItems="center">
                    <img
                      src={logoPrint}
                      alt="TowHelp Official Logo"
                      style={{ height: 110, width: "auto", objectFit: "contain" }}
                    />
                  </Box>
                </Box>

                {/* Details Grid */}
                <Grid container spacing={2} mb={2}>
                  {/* Customer Info */}
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ p: 2, backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "8px" }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#FFAE00", mb: 1, fontFamily: "Poppins, Ozen" }}>
                        👤 بيانات العميل:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "Poppins, Ozen" }}><strong>الاسم بالكامل:</strong> {order.clientName}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "Poppins, Ozen" }}><strong>رقم الهاتف:</strong> {order.clientPhone}</Typography>
                    </Box>
                  </Grid>

                  {/* Car Info */}
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ p: 2, backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "8px" }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#FFAE00", mb: 1, fontFamily: "Poppins, Ozen" }}>
                        🚘 بيانات السيارة:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "Poppins, Ozen" }}><strong>الماركة والموديل:</strong> {order.carBrand} {order.carModel}</Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "Poppins, Ozen" }}><strong>اللون:</strong> {order.carColor || "غير محدد"}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "Poppins, Ozen" }}><strong>أرقام/حروف اللوحة:</strong> {order.carPlateLetters || ""} {order.carPlateNumbers || ""}</Typography>
                    </Box>
                  </Grid>

                  {/* Service Details */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, backgroundColor: "#fff9e6", border: "2px solid #FFAE00", borderRadius: "8px" }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#b87d00", mb: 1, fontFamily: "Poppins, Ozen" }}>
                        🛠️ الخدمة والباقة المطلوبة للتنفيذ:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: "#111", fontFamily: "Poppins, Ozen" }}>
                        {order.serviceName} {order.packageName ? `— الباقة: ${order.packageName}` : ""}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#d97706", mt: 1, fontFamily: "Poppins, Ozen" }}>
                        إجمالي المبلغ المطلوب: {order.totalPrice || order.packagePrice || order.price || "حسب المعاينة"} EGP
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Location & Notes */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "8px" }}>
                      <Typography variant="body1" sx={{ mb: 0.5, fontFamily: "Poppins, Ozen" }}><strong>📍 العنوان / موقع الاستلام:</strong> {order.location || "غير محدد"}</Typography>
                      {order.notes && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic", color: "#333", fontFamily: "Poppins, Ozen" }}>
                          <strong>📝 ملاحظات وتعليمات العميل:</strong> {order.notes}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* Signatures */}
                <Box display="flex" justifyContent="space-between" mt={4} pt={2} sx={{ borderTop: "2px dashed #888" }} dir="rtl">
                  <Box textAlign="center" width="45%">
                    <Typography variant="body2" fontWeight="bold">توقيع واستلام الفني / المسؤول:</Typography>
                    <Box sx={{ height: 45, borderBottom: "1px solid #000", mt: 1 }} />
                  </Box>
                  <Box textAlign="center" width="45%">
                    <Typography variant="body2" fontWeight="bold">توقيع وموافقة العميل:</Typography>
                    <Box sx={{ height: 45, borderBottom: "1px solid #000", mt: 1 }} />
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Custom Admin Alert Dialog */}
      <Dialog
        open={customAlert !== null && customAlert.open}
        onClose={() => setCustomAlert(null)}
        PaperProps={{
          sx: {
            background: "#18181B",
            border: "1px solid rgba(255, 174, 0, 0.3)",
            borderRadius: "20px",
            padding: "24px",
            color: "#fff",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.8)",
          }
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 1.5, display: "flex", alignItems: "center", gap: 1.5, color: "#FFAE00" }}>
          <WarningAmberIcon sx={{ fontSize: "1.75rem" }} />
          <Typography component="span" variant="h6" fontWeight="bold">
            {customAlert?.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <Typography sx={{ color: "#ddd", fontSize: "0.95rem", lineHeight: 1.6 }}>
            {customAlert?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setCustomAlert(null)}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "12px",
              px: 3.5,
              py: 0.75,
              "&:hover": {
                background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Admin Confirm Dialog */}
      <Dialog
        open={customConfirm !== null && customConfirm.open}
        onClose={() => setCustomConfirm(null)}
        PaperProps={{
          sx: {
            background: "#18181B",
            border: "1px solid rgba(255, 174, 0, 0.3)",
            borderRadius: "20px",
            padding: "24px",
            color: "#fff",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.8)",
          }
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 1.5, display: "flex", alignItems: "center", gap: 1.5, color: "#FFAE00" }}>
          <WarningAmberIcon sx={{ fontSize: "1.75rem" }} />
          <Typography component="span" variant="h6" fontWeight="bold">
            {customConfirm?.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <Typography sx={{ color: "#ddd", fontSize: "0.95rem", lineHeight: 1.6 }}>
            {customConfirm?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            onClick={() => setCustomConfirm(null)}
            sx={{
              color: "#aaa",
              fontWeight: "bold",
              "&:hover": { color: "#fff" }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (customConfirm?.onConfirm) {
                customConfirm.onConfirm();
              }
              setCustomConfirm(null);
            }}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "12px",
              px: 3.5,
              py: 0.75,
              "&:hover": {
                background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingsAdminScreen;
